import type { EvidenceDocument, EvidenceFormValues, OwnershipConfig, ProjectCustomField } from "@lumanity/ui/evidence";
import { processFileName, type DocumentCreatePayload, type MetadataJobInfo } from "@lumanity/ui/ingestion";

/**
 * The upload flow's pure parts: one row per picked file, its extraction status
 * and the save payload. Kept free of React so they can be tested on their own.
 */

/** Project documents are private to the project. */
export const PROJECT_OWNERSHIP = "Private";

/** Ownership as the edit forms show it: always the project's, locked. */
export const PROJECT_OWNERSHIP_CONFIG: Partial<OwnershipConfig> = {
  values: ["Public", PROJECT_OWNERSHIP],
  defaultValue: PROJECT_OWNERSHIP,
  privateValue: PROJECT_OWNERSHIP,
  isLocked: () => true,
};

/** Where data-ingestion's upload sits behind PubCreator's gateway. */
export const UPLOAD_PATH = "/api/ingestion/upload-files";

export const ACCEPTED_FILE_TYPES = ".pdf,.docx,.pptx";
const ALLOWED_EXTENSIONS = new Set(["pdf", "docx", "pptx"]);

export function isAllowedDocumentFile(name: string): boolean {
  return ALLOWED_EXTENSIONS.has(name.split(".").pop()?.toLowerCase() ?? "");
}

export type ExtractionStatus = "queued" | "processing" | "done" | "failed";

export interface UploadRow {
  /** Stable React key and form key. */
  key: string;
  name: string;
  size: number;
  file?: File;
  /** The slugified name the upload stored the blob under — the document's only link to it. */
  uploadedFilename: string;
  downloadUrl: string;
  /** Correlation id the extraction job runs under until the document is saved (then re-keyed). */
  extractionId?: string;
  /** The enqueue itself failed (no job to poll). */
  enqueueFailed?: boolean;
}

let rowCounter = 0;

export function newRow(file: File): UploadRow {
  return { key: `row-${Date.now()}-${rowCounter++}`, name: file.name, size: file.size, file, uploadedFilename: "", downloadUrl: "" };
}

/** Files whose stored (slugified) name matches a document already in the project. */
export function duplicateNames(rows: UploadRow[], existingFilenames: Iterable<string>): Set<string> {
  const existing = new Set([...existingFilenames].map((f) => f.toLowerCase()));
  return new Set(rows.filter((r) => existing.has((r.uploadedFilename || processFileName(r.name)).toLowerCase())).map((r) => r.key));
}

export function extractionStatus(row: UploadRow, statuses: Record<string, MetadataJobInfo | undefined>): ExtractionStatus | undefined {
  if (row.enqueueFailed) return "failed";
  if (!row.extractionId) return undefined;
  const info = statuses[row.extractionId];
  if (!info || info.status === "Queued") return "queued";
  if (info.status === "Processing") return "processing";
  return info.status === "Done" ? "done" : "failed";
}

export const isSettled = (status: ExtractionStatus | undefined) => status === "done" || status === "failed";

export function blankValues(customFields: ProjectCustomField[]): EvidenceFormValues {
  const custom: Record<string, unknown> = {};
  customFields.forEach((cf) => {
    custom[cf.id] = cf.field_type === "multi_select" ? [] : "";
  });
  return {
    title: "",
    authors: "",
    year: "",
    description: "",
    source_type: "",
    organization: "",
    country: "",
    evidence_type: "",
    data_retention: "",
    url: "",
    language: "",
    ownership: PROJECT_OWNERSHIP,
    client_id: "",
    tags: "",
    custom,
    extra: {},
  };
}

/** A placeholder record for the evidence fields, which expect a document; nothing is saved from it. */
export function stubDocument(row: UploadRow): EvidenceDocument {
  return {
    id: row.key,
    filename: row.uploadedFilename || row.name,
    title: "",
    authors: "",
    description: "",
    tags: [],
    sourceType: "",
    organization: "",
    country: "",
    year: "",
    evidenceType: "",
    dataRetention: null,
    url: "",
    language: "",
    ownership: PROJECT_OWNERSHIP,
    clientId: null,
    createdAt: "",
    updatedAt: "",
  };
}

const orNull = (v: string) => (v.trim() ? v.trim() : null);

/** The evidence-base create body. Ownership is always the project's, whatever the form says. */
export function toCreatePayload(row: UploadRow, values: EvidenceFormValues): DocumentCreatePayload {
  return {
    filename: row.uploadedFilename || row.name,
    title: orNull(values.title),
    authors: orNull(values.authors),
    year: orNull(values.year),
    description: orNull(values.description),
    source_type: orNull(values.source_type),
    organization: orNull(values.organization),
    country: orNull(values.country),
    evidence_type: orNull(values.evidence_type),
    data_retention: orNull(values.data_retention),
    url: orNull(values.url),
    language: orNull(values.language),
    ownership: PROJECT_OWNERSHIP,
    tags: orNull(values.tags),
  };
}

/** Custom-field values worth saving: drop blanks so the project only stores what was filled in. */
export function filledCustomValues(custom: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(custom).filter(([, v]) => v !== "" && v !== null && v !== undefined && !(Array.isArray(v) && v.length === 0)),
  );
}
